import dayjs from 'dayjs';

/**
 * Etiqueta cada renovación con su `subscription_type` y normaliza el campo
 * de estado real (sin overlay de EXPIRED) a `subscription_state`.
 *
 * @param {Array<Object>} rows Lista de renovaciones tal y como las devuelve el backend
 * @param {'m2m'|'plan'} type Tipo al que pertenecen las filas
 * @returns {Array<Object>} Nuevas filas etiquetadas (no muta las originales)
 */
export function tagAndNormalize(rows, type) {
  if (!Array.isArray(rows)) return [];

  return rows.map(row => ({
    ...row,
    subscription_type: type,
    subscription_state: row.ki_subscription_state,
  }));
}

/**
 * Construye el payload para el endpoint `updateAll` a partir de filas
 * homogéneas (todas con el mismo `subscription_type`). Renombra
 * `subscription_state` al campo que espera cada backend:
 *   - m2m  → `state`
 *   - plan → `ki_subscription_state`
 *
 * @param {Array<{uuid:String, renewal_date:String, renewal_interval:Number, subscription_state:String, subscription_type:'m2m'|'plan'}>} rows
 * @returns {Array<Object>} Array de objetos listos para enviar al endpoint
 */
export function buildUpdatePayload(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const type = rows[0].subscription_type;
  const stateField = type === 'm2m' ? 'state' : 'ki_subscription_state';

  return rows.map(row => ({
    uuid: row.uuid,
    renewal_date: row.renewal_date,
    renewal_interval: parseInt(row.renewal_interval, 10),
    [stateField]: row.subscription_state,
  }));
}

/**
 * Aplica el filtro de rango de fechas sobre la lista completa de renovaciones.
 *
 *  - Si `dateRange` es null o no contiene ambos extremos, solo se incluyen
 *    las caducadas (`renewal_diff < 0`). Esto preserva el flujo de entrar
 *    desde "alertas".
 *  - Si `dateRange` viene definido (`[from, to]` como objetos dayjs), se
 *    incluyen las filas cuyo `renewal_diff` cae dentro de la franja medida
 *    en días desde hoy hasta `from`/`to`.
 *
 * @param {Array<Object>} rows Filas ya etiquetadas y normalizadas
 * @param {{ dateRange: [dayjs.Dayjs, dayjs.Dayjs]|null }} options
 * @returns {Array<Object>} Filas que pasan el filtro de rango
 */
export function applyClientFilters(rows, { dateRange }) {
  if (!Array.isArray(rows)) return [];

  const hasRange = !!(dateRange && dateRange[0] && dateRange[1]);

  if (!hasRange) {
    return rows;
  }

  const now = dayjs();
  const fromDays = dateRange[0].startOf('day').diff(now, 'day', true);
  const toDays = dateRange[1].endOf('day').diff(now, 'day', true);

  return rows.filter(row => {
    const diff = row.renewal_diff;
    return fromDays <= diff && diff <= toDays;
  });
}
